import axiosInstance from '../../services/axiosInstance';
import { CustomerContact } from './yearbook/customerContacts';
import {
  ActivityDetail,
  Assignee,
  CreatedLaunch,
  CreateLaunchInput,
  InternalNotification,
  LaunchSummary,
  MyWork,
  LaunchFiles,
  LaunchSettings,
  CalendarCandidate,
  CalendarWindow,
  ProductDetail,
  ProductHistoryEntry,
  ProductImportPreview,
  ProductInfoFilter,
  ProductInfoList,
  YearbookProductMatch,
  YearbookProductOption,
  YearbookSampleReminder,
  YearbookState,
  YearbookSuggestion,
  YearbookSheet,
  TemplateDraft,
  TemplateLaunchPreview,
  TemplateLaunchUpdate,
  Workspace,
} from './types';

const base = '/api/nyhetslanseringar';

export const launchApi = {
  async list(q = '', status = ''): Promise<{ launches: LaunchSummary[]; total: number }> {
    const response = await axiosInstance.get(`${base}/launches`, { params: { q: q || undefined, status: status || undefined } });
    return response.data;
  },
  async workspace(launchId: string): Promise<Workspace> {
    const response = await axiosInstance.get(`${base}/launches/${encodeURIComponent(launchId)}/workspace`);
    return response.data;
  },
  async activity(launchId: string, activityId: string): Promise<ActivityDetail> {
    const response = await axiosInstance.get(
      `${base}/launches/${encodeURIComponent(launchId)}/activities/${encodeURIComponent(activityId)}`,
    );
    return response.data;
  },
  async myWork(): Promise<MyWork> {
    const response = await axiosInstance.get(`${base}/my-work`);
    return response.data;
  },
  async myDepartments(): Promise<{ departments: string[]; choices: string[] }> {
    const response = await axiosInstance.get(`${base}/my-departments`);
    return response.data;
  },
  async saveDepartments(departments: string[]): Promise<{ departments: string[]; choices: string[] }> {
    const response = await axiosInstance.put(`${base}/my-departments`, { departments });
    return response.data;
  },
  async settings(): Promise<LaunchSettings> {
    const response = await axiosInstance.get(`${base}/settings`);
    return response.data;
  },
  async saveSettings(settings: LaunchSettings): Promise<LaunchSettings> {
    const response = await axiosInstance.put(`${base}/settings`, { ...settings, expectedVersion: settings.version });
    return response.data;
  },
  async calendarWindows(): Promise<CalendarWindow[]> {
    const response = await axiosInstance.get(`${base}/calendar/windows`);
    return response.data.windows;
  },
  async calendarCandidates(): Promise<CalendarCandidate[]> {
    const response = await axiosInstance.get(`${base}/calendar/candidates`);
    return response.data.candidates;
  },
  async publishCalendarWindow(input: { chain: string; rawDateText: string; confirmedYear: number }): Promise<CalendarWindow> {
    const response = await axiosInstance.post(`${base}/calendar/publish`, input);
    return response.data;
  },
  async publishManualWindow(input: { chain: string; announcement: string; familyId?: string; categories?: string[] }): Promise<CalendarWindow> {
    const response = await axiosInstance.post(`${base}/calendar/manual`, input);
    return response.data;
  },
  async publishYearbookWeek(input: { sheetId: string; weekKey: string; categories: string[] }): Promise<CalendarWindow> {
    const response = await axiosInstance.post(`${base}/calendar/yearbook-publish`, input);
    return response.data;
  },
  async reviseCalendarWindow(familyId: string, input: { expectedVersion: number; announcement: string; revisionWeek: number | null; revisionYear: number | null; categories: string[] }): Promise<CalendarWindow> {
    const response = await axiosInstance.post(`${base}/calendar/windows/${encodeURIComponent(familyId)}/revise`, input);
    return response.data;
  },
  async yearbook(): Promise<YearbookState> {
    const response = await axiosInstance.get(`${base}/yearbook`);
    return response.data;
  },
  async customerContacts(): Promise<CustomerContact[]> {
    const response = await axiosInstance.get(`${base}/yearbook/contacts`);
    return response.data.contacts;
  },
  async createCustomerContact(contact: Omit<CustomerContact, 'id'>): Promise<{ contacts: CustomerContact[] }> {
    const response = await axiosInstance.post(`${base}/yearbook/contacts`, contact);
    return response.data;
  },
  async updateCustomerContact(id: string, contact: Omit<CustomerContact, 'id'>): Promise<{ contacts: CustomerContact[] }> {
    const response = await axiosInstance.put(`${base}/yearbook/contacts/${encodeURIComponent(id)}`, contact);
    return response.data;
  },
  async deleteCustomerContact(id: string): Promise<{ contacts: CustomerContact[] }> {
    const response = await axiosInstance.delete(`${base}/yearbook/contacts/${encodeURIComponent(id)}`);
    return response.data;
  },
  async importYearbook(): Promise<{ sheets: number }> {
    const response = await axiosInstance.post(`${base}/yearbook/import`);
    return response.data;
  },
  async addYearbookSheet(name: string): Promise<YearbookSheet> {
    const response = await axiosInstance.post(`${base}/yearbook/sheets`, { name });
    return response.data;
  },
  async yearbookCommand(sheetId: string, command: Record<string, unknown>): Promise<YearbookSheet> {
    const response = await axiosInstance.post(`${base}/yearbook/sheets/${encodeURIComponent(sheetId)}/commands`, command);
    return response.data;
  },
  async searchYearbookProducts(query: string): Promise<YearbookProductOption[]> {
    const response = await axiosInstance.get(`${base}/yearbook/products/search`, { params: { q: query } });
    return response.data.products;
  },
  async yearbookProducts(sheetId: string): Promise<Record<string, YearbookProductMatch>> {
    const response = await axiosInstance.get(`${base}/yearbook/sheets/${encodeURIComponent(sheetId)}/products`);
    return response.data.matches;
  },
  async yearbookReminders(): Promise<YearbookSampleReminder[]> {
    const response = await axiosInstance.get(`${base}/yearbook/reminders`);
    return response.data.reminders;
  },
  async syncYearbookProductNames(sheetId: string, commandId: string): Promise<YearbookSheet> {
    const response = await axiosInstance.post(`${base}/yearbook/sheets/${encodeURIComponent(sheetId)}/sync-product-names`, { commandId });
    return response.data;
  },
  async productInfo(filter: ProductInfoFilter): Promise<ProductInfoList> {
    const response = await axiosInstance.get(`${base}/product-info`, { params: productInfoParams(filter) });
    return response.data;
  },
  async productInfoItem(id: string): Promise<ProductDetail> {
    const response = await axiosInstance.get(`${base}/product-info/items/${encodeURIComponent(id)}`);
    return response.data;
  },
  async productInfoHistory(id: string): Promise<ProductHistoryEntry[]> {
    const response = await axiosInstance.get(`${base}/product-info/items/${encodeURIComponent(id)}/history`);
    return response.data.history;
  },
  async createProductInfo(input: { commandId: string; patch: Record<string, unknown>; allowDuplicate?: boolean }): Promise<ProductDetail> {
    const response = await axiosInstance.post(`${base}/product-info/items`, input);
    return response.data;
  },
  async productInfoCommand(id: string, command: Record<string, unknown>): Promise<ProductDetail> {
    const response = await axiosInstance.post(`${base}/product-info/items/${encodeURIComponent(id)}/commands`, command);
    return response.data;
  },
  async exportProductInfo(filter: ProductInfoFilter): Promise<Blob> {
    const response = await axiosInstance.get(`${base}/product-info/export.csv`, { params: productInfoParams(filter), responseType: 'blob' });
    return response.data;
  },
  async previewProductInfoImport(file: File): Promise<ProductImportPreview> {
    const form = new FormData();
    form.append('file', file);
    const response = await axiosInstance.post(`${base}/product-info/import/preview`, form, { headers: { 'Content-Type': 'multipart/form-data' } });
    return response.data;
  },
  async importProductInfo(file: File, mode: 'all' | 'missing'): Promise<{ imported: number; skipped: number }> {
    const form = new FormData();
    form.append('mode', mode);
    form.append('file', file);
    const response = await axiosInstance.post(`${base}/product-info/import`, form, { headers: { 'Content-Type': 'multipart/form-data' } });
    return response.data;
  },
  async yearbookSuggestions(windowFamilyId: string, categories: string[]): Promise<YearbookSuggestion[]> {
    const response = await axiosInstance.get(`${base}/yearbook/suggestions`, {
      params: { windowFamilyId, categories: JSON.stringify(categories) },
    });
    return response.data.products;
  },
  async templateState(): Promise<{ templates: TemplateDraft[]; activeId: string | null }> {
    const response = await axiosInstance.get(`${base}/template`);
    return response.data;
  },
  async saveTemplate(template: Pick<TemplateDraft, 'id' | 'name' | 'version' | 'sourceHash' | 'phases' | 'units'>): Promise<TemplateDraft> {
    const response = await axiosInstance.put(`${base}/template/items/${encodeURIComponent(template.id)}`, { ...template, expectedVersion: template.version });
    return response.data;
  },
  async createTemplate(name: string, copyFromId: string | null): Promise<TemplateDraft> {
    const response = await axiosInstance.post(`${base}/template/items`, { name, copyFromId });
    return response.data;
  },
  async activateTemplate(id: string): Promise<{ templates: TemplateDraft[]; activeId: string | null }> {
    const response = await axiosInstance.post(`${base}/template/items/${encodeURIComponent(id)}/activate`);
    return response.data;
  },
  async templateLaunches(id: string): Promise<TemplateLaunchPreview[]> {
    const response = await axiosInstance.get(`${base}/template/items/${encodeURIComponent(id)}/launches`);
    return response.data.launches;
  },
  async applyTemplateToLaunches(id: string, launches: { id: string; expectedVersion: number }[], commandId: string): Promise<TemplateLaunchUpdate> {
    const response = await axiosInstance.post(`${base}/template/items/${encodeURIComponent(id)}/launches`, { launches, commandId });
    return response.data;
  },
  async templateRoles(): Promise<string[]> {
    const response = await axiosInstance.get(`${base}/template/roles`);
    return response.data.roles;
  },
  async assignees(q = ''): Promise<Assignee[]> {
    const response = await axiosInstance.get(`${base}/assignees`, { params: { q: q || undefined } });
    return response.data.assignees;
  },
  async notifications(): Promise<InternalNotification[]> {
    const response = await axiosInstance.get(`${base}/notifications`);
    return response.data.notifications;
  },
  async create(input: CreateLaunchInput): Promise<CreatedLaunch> {
    const response = await axiosInstance.post(`${base}/launches`, input);
    return response.data;
  },
  async activityCommand(launchId: string, activityId: string, command: Record<string, unknown>): Promise<void> {
    await axiosInstance.post(
      `${base}/launches/${encodeURIComponent(launchId)}/activities/${encodeURIComponent(activityId)}/commands`,
      command,
    );
  },
  async uploadFiles(
    launchId: string,
    input: { commandId: string; folderId: string; activityId?: string | null; files: File[] },
    onProgress?: (percent: number) => void,
  ): Promise<{ expectedVersion: number; files: LaunchFiles }> {
    const form = new FormData();
    form.append('commandId', input.commandId);
    form.append('folderId', input.folderId);
    if (input.activityId) form.append('activityId', input.activityId);
    input.files.forEach((file) => form.append('files', file));
    const response = await axiosInstance.post(`${base}/launches/${encodeURIComponent(launchId)}/files`, form, {
      headers: { 'Content-Type': 'multipart/form-data' },
      onUploadProgress: (event) => {
        if (!onProgress || !event.total) return;
        onProgress(Math.min(100, Math.round((event.loaded / event.total) * 100)));
      },
    });
    return response.data;
  },
  async downloadFile(launchId: string, fileId: string): Promise<Blob> {
    try {
      const response = await axiosInstance.get(
        `${base}/launches/${encodeURIComponent(launchId)}/files/${encodeURIComponent(fileId)}/download`,
        { responseType: 'blob' },
      );
      return response.data;
    } catch (error: any) {
      if (error?.response?.data instanceof Blob) {
        const text = await error.response.data.text();
        try { error.response.data = JSON.parse(text); } catch { /* svaret var ingen JSON */ }
      }
      throw error;
    }
  },
  async fileCommand(launchId: string, command: Record<string, unknown>): Promise<{ expectedVersion: number; files: LaunchFiles }> {
    const response = await axiosInstance.post(
      `${base}/launches/${encodeURIComponent(launchId)}/file-commands`,
      command,
    );
    return response.data;
  },
  async launchCommand(launchId: string, command: Record<string, unknown>): Promise<CreatedLaunch> {
    const response = await axiosInstance.post(
      `${base}/launches/${encodeURIComponent(launchId)}/launch-commands`,
      command,
    );
    return response.data;
  },
};

const productInfoParams = (filter: ProductInfoFilter) => Object.fromEntries(
  Object.entries(filter).filter(([, value]) => typeof value === 'string' && value.trim() !== ''),
);

export const apiErrorMessage = (error: any, fallback: string): string => {
  const message = error?.response?.data?.message;
  return typeof message === 'string' && message.trim() ? message : fallback;
};
